import * as React from "react";

import {
  Dialog,
  Field,
  RobotoButton,
  TextInput,
} from "@/shared/foundation/primitives";

import { isDeleteConfirmed } from "./deleteAgentConfirm";
import styles from "./sections.module.css";

export interface DeleteAgentDialogProps {
  open: boolean;
  /**
   * Fired when the person cancels (the button or Escape). Never fired while
   * `isDeleting`: a delete in flight keeps the dialog up, so a late failure
   * still has somewhere to show.
   */
  onOpenChange: (open: boolean) => void;
  /** The agent's name, which the person types to confirm. */
  resourceName: string;
  onDelete: () => void;
  isDeleting: boolean;
  /** Why the last delete failed, shown above the consequences. */
  errorMessage?: string;
}

/**
 * Asks for the agent's name before deleting it, and says what deleting does.
 *
 * Shaped like Kumo's `DeleteResource` block, on our `Dialog` in its
 * `alertdialog` role: the backdrop does not dismiss, Escape and Cancel do.
 * The caller owns the delete and closes the dialog once it succeeds.
 */
export function DeleteAgentDialog({
  open,
  onOpenChange,
  resourceName,
  onDelete,
  isDeleting,
  errorMessage,
}: DeleteAgentDialogProps) {
  const [typed, setTyped] = React.useState("");
  const isConfirmed = isDeleteConfirmed(typed, resourceName);

  return (
    <Dialog
      role="alertdialog"
      open={open}
      onOpenChange={(next) => {
        if (!next && isDeleting) {
          return;
        }
        onOpenChange(next);
      }}
      onOpenChangeComplete={(isOpen) => {
        if (!isOpen) {
          setTyped("");
        }
      }}
    >
      <Dialog.Content data-testid="agent-delete-dialog">
        <form
          className={styles["delete-form"]}
          onSubmit={(event) => {
            event.preventDefault();
            if (isConfirmed && !isDeleting) {
              onDelete();
            }
          }}
        >
          <Dialog.Title>Delete {resourceName}?</Dialog.Title>
          {errorMessage !== undefined && (
            <p
              className={styles["delete-error"]}
              role="alert"
              data-testid="agent-delete-error"
            >
              {errorMessage}
            </p>
          )}
          <Dialog.Description className={styles["delete-consequences"]}>
            This removes the agent for everyone in the organization, and it
            cannot be undone.
          </Dialog.Description>
          <ul className={styles["delete-consequences-list"]}>
            <li>Its threads are kept and stay readable under Threads.</li>
            <li>
              Triggers that launch this agent stay enabled and will record a
              failed run each time they fire, until you point them at another
              agent or disable them.
            </li>
          </ul>
          <Field.Root>
            <Field.Label>
              Type <strong>{resourceName}</strong> to confirm
            </Field.Label>
            <Field.Control
              render={
                <TextInput
                  autoFocus
                  autoComplete="off"
                  spellCheck={false}
                  data-testid="agent-delete-confirm-input"
                />
              }
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
            />
          </Field.Root>
          <div className={styles["delete-actions"]}>
            <Dialog.Close
              disabled={isDeleting}
              render={
                <RobotoButton
                  variant="secondary"
                  disabled={isDeleting}
                  data-testid="agent-delete-cancel"
                >
                  Cancel
                </RobotoButton>
              }
            />
            <RobotoButton
              type="submit"
              variant="destructive"
              disabled={!isConfirmed || isDeleting}
              loading={isDeleting}
              data-testid="agent-delete-confirm"
            >
              Delete agent
            </RobotoButton>
          </div>
        </form>
      </Dialog.Content>
    </Dialog>
  );
}
